import React, { useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import type { AppDispatch } from '@app/store';
import type { OnlineOrder } from '@src/types/onlineOrder';
import { getRoomLabelText } from '@utilities/common';
import {
  countTableCartItems,
  listTableNames,
  mergeOnlineOrderIntoTableCart,
} from '@utilities/onlineOrderCartMerge';

interface OnlineOrderMergeDialogProps {
  open: boolean;
  sourceOrder: OnlineOrder | null;
  allTabs: Record<string, number>;
  restaurantCart: Record<string, Record<string, unknown>>;
  restaurantId: string;
  dispatch: AppDispatch;
  onClose: () => void;
  onMerged: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

function getOrderLabel(order: OnlineOrder): string {
  return order.orderSeqNo ?? order.orderCode;
}

function formatTableLabel(tableNumber: string): string {
  const [tab, index] = tableNumber.split('-');
  const parsedIndex = parseInt(index, 10);
  return getRoomLabelText(tab, Number.isFinite(parsedIndex) ? parsedIndex : 1);
}

export default function OnlineOrderMergeDialog({
  open,
  sourceOrder,
  allTabs,
  restaurantCart,
  restaurantId,
  dispatch,
  onClose,
  onMerged,
  onError,
  onSuccess,
}: OnlineOrderMergeDialogProps) {
  const [destinationTable, setDestinationTable] = useState('');
  const [merging, setMerging] = useState(false);

  const tableOptions = useMemo(() => listTableNames(allTabs), [allTabs]);

  const handleClose = () => {
    if (merging) return;
    setDestinationTable('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!sourceOrder || !destinationTable) {
      onError('Please select a destination table.');
      return;
    }

    setMerging(true);
    try {
      const destTable = await mergeOnlineOrderIntoTableCart({
        dispatch,
        restaurantId,
        order: sourceOrder,
        destinationTable,
        restaurantCart,
        allTabs,
      });

      onSuccess(
        `Online order ${getOrderLabel(sourceOrder)} merged into ${formatTableLabel(destTable).toUpperCase()}.`
      );
      setDestinationTable('');
      onMerged();
      onClose();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Could not merge into table cart.');
    } finally {
      setMerging(false);
    }
  };

  const sourceLabel = sourceOrder ? getOrderLabel(sourceOrder) : '';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeTypeIcon color="secondary" />
        Merge into table cart
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Move all items from online order <strong>{sourceLabel}</strong> into a running POS
          table cart. The online order stays in the list for reference.
        </Typography>

        {tableOptions.length === 0 ? (
          <Typography variant="body2" color="warning.main">
            No tables configured for this restaurant.
          </Typography>
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            margin="normal"
            label="Destination table"
            value={destinationTable}
            onChange={(event) => setDestinationTable(event.target.value)}
            disabled={merging}
          >
            {tableOptions.map((table) => {
              const itemCount = countTableCartItems(restaurantCart[table]);
              return (
                <MenuItem key={table} value={table}>
                  {formatTableLabel(table)}
                  {itemCount > 0 ? ` · ${itemCount} item(s) in cart` : ' · empty'}
                </MenuItem>
              );
            })}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={merging}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="outlined"
          disabled={merging || !destinationTable || tableOptions.length === 0}
          startIcon={merging ? <CircularProgress size={16} /> : undefined}
        >
          {merging ? 'Merging…' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
